import { createLogger } from 'shared/services/logger';
import { ImageId } from 'shared/stores/annotations/legacy-translations';
import { store } from 'shared/stores/core/decorators';
import { pull } from 'shared/util';

const logger = createLogger('vil:extensions:PdnStore');

@store('PDN')
export class PdnStore {
  state = {
    // Is the PDN visible?
    isVisible: false,
    // Is data for the PDN loaded? Note that we always load when the PDN is toggled on. Once visible we track region
    // add / delete events to update the "pins" list. If isLoaded is false we skip updates.
    isLoaded: false,
    // List of pins from the server
    pins: [],
    // The currently selected index (plus one...)
    selectedIndex: 1,

    error: null
  };

  AFTER_VIEWPORT_CREATED ({get, dispatch}, viewport) {
    const uScope = get('USCOPE/uScope');
    const state = this.state;

    viewport.events.on('us:annotations:region-change', function ({imageId, regionId, anShape, action}) {
      // Normalize legacy types
      imageId = ImageId(imageId);
      regionId = Number(regionId);

      if (!imageId || !regionId || !action) {
        logger.error('us:annotations:region-change: Event is missing needed data.',
          'action', action,
          'imageId:', imageId,
          'regionId:', regionId);
        return;
      }

      // If the panel is hidden or the pindrop panel is hidden don't do any of this
      if (!state.isVisible || anShape !== 'pin') {
        return;
      }

      switch (action) {
        case 'sel':
          dispatch('PDN_SET_REGION_SELECTED', {regionId});
          break;
        case 'add':
          state.pins.push({
            regionId,
            imageId,
            fullUrl: uScope.getThisImageById('@' + imageId).getFullURL(),
            displayOrder: state.pins.length
          });
          dispatch('PDN_SET_REGION_SELECTED', {regionId});
          break;
        case 'del':
          const pin = state.pins.find(p => p.regionId === regionId);
          if (!pin) {
            logger.error('us:annotations:region-change ("delete"): Could not find pin for regionId', regionId, 'in', state.pins);
            return;
          }

          // Remove the pin
          let index = state.pins.indexOf(pin);
          pull(state.pins, pin);

          // update the display order for the regions following the deleted region
          for (let i = index; i < state.pins.length; i++) {
            state.pins[i].displayOrder--;
          }

          if (state.selectedIndex > 0) {
            // note, when a pindrop is deleted, uScope first sends a 'sel' action for the new region to select
            // before it sends the 'del', so need to reselect the previously selected pindrop here
            // also note, uScope sends the 'sel' only if there are remaining regions on the current image
            dispatch('PDN_SET_REGION_SELECTED', {index: state.selectedIndex});
          } else {
            // this happens when there are no more regions on the current image
            if (index < 0) {
              index = 0;
            } else if (index >= state.pins.length) {
              index = state.pins.length - 1;
            }
            dispatch('PDN_SET_REGION_SELECTED', {}); // TODO: //?
          }
          break;
      }
    });
  }

  PDN_TOGGLE_NAVIGATOR ({dispatch}) {
    this.state.isVisible = !this.state.isVisible;
    if (this.state.isVisible) {
      dispatch('PDN_LOAD_PINS');
    }
  }

  async PDN_LOAD_PINS ({get, dispatch, resources}) {
    const provider = resources.get('PDN');
    const vil = get('VIL/instance');
    const settings = vil.getConfiguration();

    // Collect and configure the data we need
    let ids = [];
    if (settings.tableName.toLowerCase() === 'slide') {
      ids.push(...settings.dstInfo.cases[0].slides.map(s => s.id));
    } else if (settings.priorId) {
      ids.push(settings.priorId);
    } else {
      ids.push(settings.currentId);
    }

    // Clear the pins and then request data
    this.state.isLoaded = false;
    this.state.pins.splice(0);
    this.state.error = null;
    let results;
    try {
      results = await provider.list(settings.tableName, ids);
    } catch (e) {
      this.state.isLoaded = true;
      let message = 'Error loading pins.';
      if (e.response) {
        message = e.response.data;
      } else if (e.asMessage) {
        message = e.asDetails || e.asMessage;
      }
      this.state.error = message;
      return;
    }

    // Async check
    if (!this.state.isVisible) {
      logger.warn('PDN_LOAD_PINS: Pindrop navigator was hidden before results loaded.');
      return;
    }

    // If there's data load it up
    if (results?.data?.returnedCount > 0) {
      this.state.pins.push(...results.data.response);
      dispatch('PDN_SELECT_PIN', {index: 0});
    }
    this.state.isLoaded = true;
  }

  PDN_SELECT_PIN ({state, get}, {index}) {
    const {selectedImageId} = state('IMAGE');
    const uScope = get('USCOPE/uScope');
    const vil = get('VIL/instance');
    const vilConfig = vil.getConfiguration();

    let pin = this.state.pins[index];
    if (!pin) {
      logger.error('PDN_SET_REGION_SELECTED: Could not find pin for index', index, this.state.pins);
      return;
    }
    const {imageId, regionId} = pin;

    if (imageId !== selectedImageId && !isImagePinned(uScope, imageId)) {
      // If the pin is not on the current view we need to change the image AND THEN wait for its annotations service to
      // restore layers before we set the region id. Setup the event listener on the root event channel for since the
      // viewport we want to listen on hasn't been loaded yet.
      uScope.events.once('us:annotations:layers-loaded', () => {
        uScope.goToRegion(imageId, regionId);
      });

      if (vilConfig.displayDST) {
        // select the slide in the tray; this will automatically replace it in uScope
        vil.getDst().selectImageByImageId(imageId);
      } else {
        const imagePath = '@' + selectedImageId;
        const image = {
          imagePath,
          fullURL: pin.fullURL,
          regionId
        };
        vil.uScopeImageUpdate('replace', {image: {imagePath}}, {image});
      }
    } else {
      uScope.goToRegion(imageId, regionId);
    }
  }

  PDN_SET_REGION_SELECTED (store, {regionId}) {
    const pin = this.state.pins.find(p => p.regionId === regionId);
    if (!pin) {
      logger.error('PDN_SET_REGION_SELECTED: Could not find pin for regionId', regionId);
      return;
    }

    const index = this.state.pins.indexOf(pin) + 1;

    if (index !== this.state.selectedIndex) {
      logger.log('PDN_SET_REGION_SELECTED: Setting selectedIndex to', index);
      this.state.selectedIndex = index;
    }
  }
}

const isImagePinned = function (uScope, imagePath) {
  const images = uScope.getStateArray();
  if (images.length === 1) {
    return false;
  }
  return !!uScope.getStateArray().find(
    (i, val) => (val.imagePath === imagePath && val.isPinned)
  );
};



// WEBPACK FOOTER //
// ./src/vil/uScope-extensions/PinDropNavigator/PdnStore.js